/**
 * Image Replacement.
 *
 * Previously shipped inside free's Images.js (loadDarkImage/loadLightImage/
 * replaceImage/replaceImages + a MutationObserver for lazy-loaded images),
 * gated by image_replaces being forced empty for free installs. WordPress.org
 * guideline 5 does not permit shipping locked feature code in the free
 * plugin, so it lives here and free's Images.js no longer contains this
 * logic at all - only the always-free background-image swap feature
 * (replaceBackgroundImage/replaceBackgroundImages/getDarkSrc) remains there.
 *
 * getDarkSrc() below is a deliberate duplicate of free's own private
 * getDarkSrc() closure (Images.js), not shared with it - free's copy is
 * unreachable from this separate bundle even via window.WPDarkModeImages
 * (that global only exposes { init }, not the private lookup function).
 * This mirrors the existing precedent in Premium.js::extendVideos(), which
 * duplicates video's getDarkSrc rather than sharing it. If the matching rule
 * (currently: strip the query string before comparing URLs) ever changes,
 * both copies need updating together.
 *
 * Images.js exposes no per-feature methods to monkey-patch onto (only
 * { init }), so this is a standalone reimplementation - following
 * Accessibility.js/CustomTriggers.js/ImageFilters.js's call-once,
 * DOMContentLoaded-only pattern, and re-attaching its own listener on the
 * same wp_dark_mode event free's Images.js fires on toggle.
 *
 * Copied verbatim from free's last working copy so behaviour is identical
 * to before.
 */
class ImageReplacement {
	/**
	 * Init. Call exactly once, from DOMContentLoaded.
	 */
	init() {
		if (typeof wp_dark_mode_json === 'undefined') {
			return;
		}

		const WPDarkMode = window.WPDarkMode;

		if (!WPDarkMode) {
			return;
		}

		this.replaceImages();
		this.replaceBackgroundImages();

		document.addEventListener('wp_dark_mode', () => {
			this.replaceImages();
			this.replaceBackgroundImages();
		});

		if (!wp_dark_mode_json.options.performance_track_dynamic_content) {
			return;
		}

		// Create a MutationObserver to watch for src attribute changes.
		let mutationThrottle = null;
		const observer = new MutationObserver((mutations) => {
			if (mutationThrottle) return;
			mutationThrottle = true;
			setTimeout(() => {
				mutationThrottle = null;
			}, 500);

			mutations.forEach((mutation) => {
				if (mutation.type === 'attributes' && mutation.attributeName === 'src') {
					const img = mutation.target;
					// Only process if the src was actually set by lazy loading (not by us).
					if (img.src && img.src.startsWith('http') && !img.dataset.processed) {
						img.dataset.processed = 'true';
						this.replaceImage(img);
					}
				}
			});
		});

		// Observe all img elements for src attribute changes.
		document.querySelectorAll('img').forEach((img) => {
			observer.observe(img, {
				attributes: true,
				attributeFilter: ['src'],
			});
		});
	}

	/**
	 * Get dark src. Duplicate of free's private getDarkSrc() - see file
	 * header for why this can't be shared instead.
	 */
	getDarkSrc = (src) => {
		if (!src) return;

		const replaceable_images = wp_dark_mode_json.options.image_replaces || false;
		if (!replaceable_images) return false;

		const imagePair = replaceable_images.find((img) => {
			// Remove query string from image src.
			const srcWithoutQuery = src.split('?')[0];

			return img.light === srcWithoutQuery;
		});

		if (!imagePair) return false;

		return imagePair.dark;
	}

	loadDarkImage = (image) => {
		// For lazy loaded.
		image.srcset = image.srcset || image.dataset.srcset || image.dataset.lazySrcset || '';
		image.src = image.src || image.dataset.src || image.dataset.lazySrc || '';

		// If has srcset.
		if (image.srcset) {
			// Only ever store the srcset as it was before any swap. Running
			// again while already dark would otherwise record the dark srcset
			// as the "light" one, and light mode could never restore it.
			if (typeof image.dataset.lightSrcset === 'undefined') {
				image.dataset.lightSrcset = image.srcset;
			}

			// Swap from the stored original, not from whatever is on the
			// element now, so repeated runs stay idempotent.
			image.srcset = this.getDarkSrcset(image.dataset.lightSrcset);
		}

		if (image.src) {
			const darkSrc = this.getDarkSrc(image.src);
			if (darkSrc) {
				if (typeof image.dataset.lightSrc === 'undefined') {
					image.dataset.lightSrc = image.src;
				}
				image.src = darkSrc;
			}
		}
	}

	/**
	 * Dark equivalent of a full srcset.
	 *
	 * WordPress emits one candidate per registered image size
	 * ("logo.png 512w, logo-300x300.png 300w, ..."), so a plain replace of the
	 * configured light URL only ever rewrites the full-size candidate and
	 * leaves the resized ones pointing at the light image - the browser then
	 * picks a light variant by viewport while dark mode is on. Each candidate
	 * is resolved on its own, and a sized candidate falls back to the pair's
	 * dark URL when its own size has no configured replacement.
	 */
	getDarkSrcset = (srcset) => {
		if (!srcset) return srcset;

		return srcset.split(',').map((candidate) => {
			const parts = candidate.trim().split(/\s+/);
			const url = parts[0];
			if (!url) return candidate;

			const descriptor = parts.slice(1).join(' ');
			const darkUrl = this.getDarkSrc(url) || this.getDarkSrcForSizedUrl(url);

			if (!darkUrl) return candidate;

			return descriptor ? `${darkUrl} ${descriptor}` : darkUrl;
		}).join(', ');
	}

	/**
	 * Dark URL for a WordPress-resized variant of a configured light image,
	 * e.g. "logo-300x300.png" when the pair was set up with "logo.png".
	 */
	getDarkSrcForSizedUrl = (url) => {
		const withoutQuery = url.split('?')[0];
		// "…/logo-300x300.png" -> "…/logo.png"
		const unsized = withoutQuery.replace(/-\d+x\d+(\.[a-z0-9]+)$/i, '$1');

		if (unsized === withoutQuery) return false;

		return this.getDarkSrc(unsized);
	}

	loadLightImage = (image) => {
		if (image.dataset.lightSrcset) {
			image.srcset = image.dataset.lightSrcset;
		}

		if (image.dataset.lightSrc) {
			image.src = image.dataset.lightSrc;
		}
	}

	replaceImage = (image) => {
		if (!image) return;

		if (image.classList.contains('wp-dark-mode-ignore')) {
			return;
		}

		if (window.WPDarkMode.isActive) {
			this.loadDarkImage(image);
		} else {
			this.loadLightImage(image);
		}
	}

	replaceImages = () => {
		if (!wp_dark_mode_json.options.image_replaces.length) return;

		const images = document.querySelectorAll('img, picture img, img[srcset], picture source, picture source[srcset]');

		if (!images || !images.length) return;

		images.forEach(this.replaceImage);
	}

	/**
	 * Dark/light swap for CSS background images. Previously shipped in free's
	 * Images.js; moved here so the light version contains no image-replacement
	 * logic at all. Free's Images.js still restores element.dataset.lightSrc on
	 * toggle back to light, keyed off the wp-dark-mode-bg-image class set below.
	 *
	 * Replaced elements (img/video/iframe/picture/source) are excluded from
	 * BG_SELECTORS: they carry their own source via replaceImages() above, and
	 * giving them a CSS background paints a stray layer behind the element.
	 */
	BG_SELECTORS = 'body *:not(.wp-dark-mode-ignore):not(.wp-dark-mode-ignore *):not(img):not(video):not(iframe):not(picture):not(source):not(script):not(style)';

	hasBgImage = (el) => {
		return window.getComputedStyle(el)?.getPropertyValue('background-image') !== 'none' ||
			window.getComputedStyle(el, '::after')?.getPropertyValue('background-image') !== 'none' ||
			window.getComputedStyle(el, '::before')?.getPropertyValue('background-image') !== 'none';
	}

	replaceBackgroundImage = (element) => {
		if (!element) return;
		if (!this.hasBgImage(element)) return;

		let backgroundImage = window.getComputedStyle(element).getPropertyValue('background-image');
		backgroundImage = backgroundImage.replace(/url\((['"])?(.*?)\1\)/gi, '$2').split(',')[0];

		if (window.WPDarkMode.isActive) {
			const darkSrc = this.getDarkSrc(backgroundImage);
			if (darkSrc) {
				// Marked only once a pair actually matched, so the class stays
				// an accurate record of "this element's background was swapped"
				// - free's Images.js keys its light restore off it.
				element.classList.add('wp-dark-mode-bg-image');
				element.style.backgroundImage = `url(${darkSrc})`;
				element.dataset.lightSrc = backgroundImage;
				element.dataset.darkSrc = darkSrc;
			}
		}
		// Light-mode restore is handled by free's Images.js (dataset.lightSrc).
	}

	replaceBackgroundImages = () => {
		if (!wp_dark_mode_json.options.image_replaces || !wp_dark_mode_json.options.image_replaces.length) return;

		document.querySelectorAll(this.BG_SELECTORS).forEach(this.replaceBackgroundImage);
	}
}

export default new ImageReplacement();
